import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Share2, Download, TrendingUp, Users, Target, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Footer, { PlatformDisclaimer } from '@/components/Footer';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const ViralReport = () => {
  const { creatorId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReport();
  }, [creatorId]);

  const loadReport = async () => {
    try {
      const response = await axios.get(`${API}/creators/${creatorId}/report`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const shareToLinkedIn = () => {
    const text = `I just discovered my sponsorship score on Sponsiq! 🎯 My channel ${report.creator_name} has a ${report.sponsorship_score}% sponsorship readiness score.`;
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  const shareToTwitter = () => {
    const text = `🎯 Just got my sponsorship score on @Sponsiq!\n\n${report.creator_name}\n📊 Score: ${report.sponsorship_score}%\n👥 ${report.subscriber_count?.toLocaleString()} subscribers\n💰 Est. Value: $${report.estimated_value_min}-$${report.estimated_value_max}\n\nCheck your score:`;
    const url = window.location.origin;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const downloadReport = () => {
    // Simple download trigger - in production this would generate a PDF
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Report not found</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <Link to="/">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="get-your-score-btn">
              Get Your Score
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Report Card */}
        <Card className="p-12 mb-8 bg-white shadow-xl" data-testid="viral-report-card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <Target className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
              Sponsorship Report
            </h1>
            <p className="text-slate-600">Generated by Sponsiq</p>
          </div>

          {/* Creator Info */}
          <div className="mb-8 p-6 bg-slate-50 rounded-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="report-creator-name">
              {report.creator_name}
            </h2>
            <div className="flex items-center gap-6 text-slate-600">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="font-semibold" data-testid="report-subscribers">{report.subscriber_count?.toLocaleString()} subscribers</span>
              </div>
              <span>•</span>
              <span className="font-semibold" data-testid="report-niche">{report.niche}</span>
            </div>
          </div>

          {/* Sponsorship Score */}
          <div className="mb-8">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 text-center">Sponsorship Readiness Score</p>
            <div className="flex items-center justify-center gap-6 mb-4">
              <div className="text-center">
                <div className="text-7xl font-bold text-indigo-600 mb-2" style={{fontFamily: 'JetBrains Mono'}} data-testid="report-score">
                  {report.sponsorship_score}%
                </div>
                <div className="w-64 h-4 bg-slate-100 rounded-full overflow-hidden mx-auto">
                  <div className="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 rounded-full" style={{width: `${report.sponsorship_score}%`}}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="p-6 bg-gradient-to-br from-indigo-50 to-slate-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sponsorship Probability</p>
              </div>
              <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="report-probability">
                {report.sponsorship_probability}%
              </p>
            </div>

            <div className="p-6 bg-gradient-to-br from-green-50 to-slate-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-6 h-6 text-green-600" />
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Estimated Deal Value</p>
              </div>
              <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}} data-testid="report-value">
                ${report.estimated_value_min?.toLocaleString()} - ${report.estimated_value_max?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Top Brand Matches */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Top Brand Matches</h3>
            <div className="space-y-3">
              {report.top_brands?.slice(0, 5).map((brand, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid={`report-brand-${index}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <span className="font-semibold text-slate-900">{brand.name}</span>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                    {brand.score}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center p-6 bg-gradient-to-r from-indigo-600 to-indigo-500 rounded-lg text-white">
            <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Manrope'}}>Want to unlock your full report?</h3>
            <p className="mb-4">Discover all your brand matches and start connecting with sponsors</p>
            <Link to="/">
              <Button className="bg-white text-indigo-600 hover:bg-slate-50 font-semibold" data-testid="unlock-report-btn">
                Get Your Full Report Free
              </Button>
            </Link>
          </div>
        </Card>

        {/* Share Buttons */}
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-900 mb-4">Share Your Report</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button
              onClick={shareToLinkedIn}
              className="bg-[#0077B5] hover:bg-[#006399] text-white"
              data-testid="share-linkedin-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              LinkedIn
            </Button>
            <Button
              onClick={shareToTwitter}
              className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
              data-testid="share-twitter-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              X (Twitter)
            </Button>
            <Button
              onClick={copyShareLink}
              variant="outline"
              data-testid="copy-link-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button
              onClick={downloadReport}
              variant="outline"
              data-testid="download-report-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
        
        {/* Platform Disclaimer */}
        <PlatformDisclaimer />
      </div>
      
      {/* Footer */}
      <Footer showDisclaimer={false} />
    </div>
  );
};

export default ViralReport;
